import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { getAdmissionApiUrl } from '../../config/globalConfig';
import SuccessModal from '../SuccessModal';
import './AdmissionFormSec.css';

export default function AdmissionFormSec() {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        contactNumber: '',
        gender: '',
        dob: '',
        age: '',
        address: '',
        city: '',
        country: '',
        nationality: '',
        occupation: '',
        education: '',
        selectedCourses: [], // Array of {course, type} objects
        training: '',
        goal: '',
        howKnow: '',
        paymentMethod: '', // 'cash' or 'online'
        nationalityId: null,
        paymentScreenshot: null,
        terms: false,
        privacy: false,
        notRobot: false
    });

    const [currentCourse, setCurrentCourse] = useState('');
    const [currentCourseType, setCurrentCourseType] = useState('');

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    
    // Refs for file inputs
    const nationalityIdRef = useRef(null);
    const paymentScreenshotRef = useRef(null);

    // Validation functions
    const validateField = (name, value, type = 'text') => {
        let error = '';

        switch (name) {
            case 'fullName':
                if (!value.trim()) error = 'Full name is required';
                else if (value.trim().length < 2) error = 'Full name must be at least 2 characters';
                else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Full name can only contain letters and spaces';
                break;

            case 'email':
                if (!value.trim()) error = 'Email is required';
                else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) error = 'Please enter a valid email address';
                break;

            case 'contactNumber':
                if (!value.trim()) error = 'Contact number is required';
                else {
                    // Remove all non-digit characters for validation
                    const digitsOnly = value.replace(/\D/g, '');
                    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
                        error = 'Contact number must be between 10-15 digits';
                    } else if (!/^[\+]?[0-9\s\-\(\)]{10,20}$/.test(value)) {
                        error = 'Please enter a valid contact number';
                    }
                }
                break;

            case 'gender':
                if (!value) error = 'Please select your gender';
                break;

            case 'dob':
                if (!value) error = 'Date of birth is required';
                else {
                    const dobDate = new Date(value);
                    const today = new Date();
                    
                    // Check if date is valid
                    if (isNaN(dobDate.getTime())) {
                        error = 'Please enter a valid date';
                    } else {
                        // Calculate exact age considering month and day
                        let age = today.getFullYear() - dobDate.getFullYear();
                        const monthDiff = today.getMonth() - dobDate.getMonth();
                        const dayDiff = today.getDate() - dobDate.getDate();
                        
                        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                            age--;
                        }
                        
                        if (dobDate > today) {
                            error = 'Date of birth cannot be in the future';
                        } else if (age > 100) {
                            error = 'Age cannot exceed 100 years';
                        } else if (dobDate.getFullYear() < 1900) {
                            error = 'Please enter a valid date of birth (after 1900)';
                        }
                    }
                }
                break;

            case 'age':
                if (!value) error = 'Age is required';
                else if (isNaN(value) || value < 5 || value > 100) error = 'Please enter a valid age (5-100)';
                break;

            case 'address':
                if (!value.trim()) error = 'Address is required';
                else if (value.trim().length < 10) error = 'Address must be at least 10 characters';
                break;

            case 'city':
                if (!value.trim()) error = 'City is required';
                else if (value.trim().length < 2) error = 'City must be at least 2 characters';
                break;

            case 'country':
                if (!value.trim()) error = 'Country is required';
                else if (value.trim().length < 2) error = 'Country must be at least 2 characters';
                break;

            case 'nationality':
                if (!value.trim()) error = 'Nationality is required';
                else if (value.trim().length < 2) error = 'Nationality must be at least 2 characters';
                break;

            case 'occupation':
                if (!value.trim()) error = 'Occupation is required';
                else if (value.trim().length < 2) error = 'Occupation must be at least 2 characters';
                break;

            case 'education':
                if (!value.trim()) error = 'Educational qualification is required';
                else if (value.trim().length < 2) error = 'Educational qualification must be at least 2 characters';
                break;

            case 'selectedCourses':
                if (!value || value.length === 0) error = 'Please select at least one course and its type';
                break;

            case 'howKnow':
                if (!value.trim()) error = 'Please tell us how you found us';
                else if (value.trim().length < 5) error = 'Please provide more details (at least 5 characters)';
                break;

            case 'paymentMethod':
                if (!value) error = 'Please select a payment method';
                break;

            case 'nationalityId':
                if (value && value.size > 5 * 1024 * 1024) error = 'File size must be less than 5MB';
                else if (value && !['image/jpeg', 'image/png', 'image/jpg'].includes(value.type)) {
                    error = 'Please upload a valid image file (PNG, JPG, JPEG only)';
                }
                break;

            case 'paymentScreenshot':
                if (!value) error = 'Payment screenshot is required';
                else if (value.size > 5 * 1024 * 1024) error = 'File size must be less than 5MB';
                else if (!['image/jpeg', 'image/png', 'image/jpg'].includes(value.type)) {
                    error = 'Please upload a valid image file (PNG, JPG, JPEG only)';
                }
                break;

            case 'terms':
            case 'privacy':
            case 'notRobot':
                if (!value) error = 'This field is required';
                break;
        }

        return error;
    };

    // Handle input changes with validation
    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        let newValue;
        if (type === 'checkbox') {
            newValue = checked;
        } else if (type === 'file') {
            newValue = files[0] || null;
        } else {
            newValue = value;
        }

        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Validate field and update errors
        const error = validateField(name, newValue, type);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Handle number-only input for contact number and age
    const handleNumberInput = (e) => {
        const { name, value } = e.target;

        // Only allow numbers and some special characters for phone numbers
        if (name === 'contactNumber') {
            // Allow numbers, spaces, hyphens, parentheses, and plus sign
            const phoneRegex = /^[\d\s\-\(\)\+]*$/;
            if (phoneRegex.test(value)) {
                handleInputChange(e);
            }
        } else if (name === 'age') {
            // Only allow numbers
            const numberRegex = /^\d*$/;
            if (numberRegex.test(value)) {
                handleInputChange(e);
            }
        }
    };

    // Handle key press to prevent non-numeric input
    const handleKeyPress = (e) => {
        const { name } = e.target;

        if (name === 'contactNumber') {
            // Allow numbers, backspace, delete, tab, enter, arrow keys, and phone special chars
            const allowedKeys = /[\d\s\-\(\)\+]|Backspace|Delete|Tab|Enter|ArrowLeft|ArrowRight|ArrowUp|ArrowDown/;
            if (!allowedKeys.test(e.key)) {
                e.preventDefault();
            }
        } else if (name === 'age') {
            // Only allow numbers and control keys
            const allowedKeys = /[\d]|Backspace|Delete|Tab|Enter|ArrowLeft|ArrowRight|ArrowUp|ArrowDown/;
            if (!allowedKeys.test(e.key)) {
                e.preventDefault();
            }
        }
    };

    // Handle course selection
    const handleCourseChange = (e) => {
        const course = e.target.value;
        setCurrentCourse(course);
        setCurrentCourseType(''); // Reset course type when course changes
    };

    // Handle course type selection
    const handleCourseTypeChange = (e) => {
        setCurrentCourseType(e.target.value);
    };

    // Add course to selected courses
    const addCourse = () => {
        if (!currentCourse || !currentCourseType) {
            toast.error('Please select both course and course type');
            return;
        }

        // Check if this course-type combination already exists
        const exists = formData.selectedCourses.some(
            item => item.course === currentCourse && item.type === currentCourseType
        );

        if (exists) {
            toast.error('This course and type combination is already selected');
            return;
        }

        const newCourse = {
            course: currentCourse,
            type: currentCourseType
        };

        setFormData(prev => ({
            ...prev,
            selectedCourses: [...prev.selectedCourses, newCourse]
        }));

        // Clear current selections
        setCurrentCourse('');
        setCurrentCourseType('');

        // Clear course validation error
        setErrors(prev => ({
            ...prev,
            selectedCourses: ''
        }));
    };

    // Remove course from selected courses
    const removeCourse = (index) => {
        setFormData(prev => ({
            ...prev,
            selectedCourses: prev.selectedCourses.filter((_, i) => i !== index)
        }));
    };

    // Get available course types based on current course selection
    const getAvailableCourseTypes = () => {
        const specialCourses = ['music-production', 'dual', 'all-in-one'];

        if (specialCourses.includes(currentCourse)) {
            return [
                { value: 'quarter', label: 'Quarter' },
                { value: 'certification', label: 'Certification' }
            ];
        } else {
            return [
                { value: 'crash', label: 'Crash' },
                { value: 'quarter', label: 'Quarter' },
                { value: 'certification', label: 'Certification' }
            ];
        }
    };

    // Get course label by value
    const getCourseLabel = (courseValue) => {
        const courseLabels = {
            'opera': 'Opera',
            'western-vocals': 'Western Contemporary Vocals',
            'hindustani-vocals': 'Hindustani Classical Vocals',
            'bollywood': 'Bollywood Film Singing',
            'guitar': 'Guitar (Acoustic/Electric)',
            'violin': 'Violin',
            'piano': 'Piano/Keyboard',
            'music-production': 'Music Production',
            'dual': 'Dual Program',
            'all-in-one': 'All-In One Program'
        };
        return courseLabels[courseValue] || courseValue;
    };

    // Get course type label by value
    const getCourseTypeLabel = (typeValue) => {
        const typeLabels = {
            'crash': 'Crash',
            'quarter': 'Quarter',
            'certification': 'Certification'
        };
        return typeLabels[typeValue] || typeValue;
    };

    // Validate entire form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Validate all required fields
        Object.keys(formData).forEach(key => {
            // Skip validation for optional fields when they are empty
            if (key === 'nationalityId' && !formData[key]) {
                return; // Skip validation for optional nationalityId when empty
            }
            
            const error = validateField(key, formData[key]);
            if (error) {
                newErrors[key] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            // toast.error('Please fix all errors before submitting');
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();

            // Append all form data
            Object.keys(formData).forEach(key => {
                if (key === 'selectedCourses') {
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                } else if (formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key]);
                }
            });

            // Submit to API
            const response = await fetch(getAdmissionApiUrl('SUBMIT_FORM'), {
                method: 'POST',
                body: formDataToSend
            });

            if (response.ok) {
                // Show success modal instead of toast
                setShowSuccessModal(true);
                // Reset form
                setFormData({
                    fullName: '',
                    email: '',
                    contactNumber: '',
                    gender: '',
                    dob: '',
                    age: '',
                    address: '',
                    city: '',
                    country: '',
                    nationality: '',
                    occupation: '',
                    education: '',
                    selectedCourses: [],
                    training: '',
                    goal: '',
                    howKnow: '',
                    paymentMethod: '',
                    nationalityId: null,
                    paymentScreenshot: null,
                    terms: false,
                    privacy: false,
                    notRobot: false
                });
                setCurrentCourse('');
                setCurrentCourseType('');
                setErrors({});
                
                // Reset file input elements
                if (nationalityIdRef.current) {
                    nationalityIdRef.current.value = '';
                }
                if (paymentScreenshotRef.current) {
                    paymentScreenshotRef.current.value = '';
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to submit form');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to submit admission form. Please try again.');
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <section id="admission-form-section">
                <div className="admission-form-container">
                    <div className="admission-form-header">
                        <h2 className="section-title">Personal Information</h2>
                    </div>

                    <div className="admission-form-content">
                        <form className="admission-form-form" onSubmit={handleSubmit}>
                            <div className="form-section">
                                <div className="form-group">
                                    <label className="form-label">FULL NAME <span>*</span></label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.fullName ? 'error' : ''}`}

                                    />

                                </div>

                                <div className="form-group">
                                    <label className="form-label">EMAIL ADDRESS <span>*</span></label>
                                    <input
                                        type="text"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.email ? 'error' : ''}`}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">CONTACT NUMBER <span>*</span></label>
                                    <input
                                        type="text"
                                        name="contactNumber"
                                        value={formData.contactNumber}
                                        onChange={handleNumberInput}
                                        onKeyPress={handleKeyPress}
                                        className={`form-input admission_input ${errors.contactNumber ? 'error' : ''}`}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">GENDER <span>*</span></label>
                                    <div className="radio-group">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="gender"
                                                value="male"
                                                checked={formData.gender === 'male'}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="radio-text">Male</span>
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="gender"
                                                value="female"
                                                checked={formData.gender === 'female'}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="radio-text">Female</span>
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="gender"
                                                value="other"
                                                checked={formData.gender === 'other'}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="radio-text">Other</span>
                                        </label>
                                    </div>
                                    {errors.gender && <div className="error-message admission_error">{errors.gender}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">DOB <span>*</span></label>
                                    <div className="date-input-wrapper">
                                        <input
                                            type="date"
                                            name="dob"
                                            value={formData.dob}
                                            onChange={handleInputChange}
                                            className={`form-input admission_input date-input ${errors.dob ? 'error' : ''}`}
                                            max={new Date().toISOString().split('T')[0]}
                                            min="1900-01-01"
                                            id="dob-input"
                                        />
                                        <button
                                            type="button"
                                            className="calendar-icon-btn"
                                            onClick={() => document.getElementById('dob-input').showPicker()}
                                            aria-label="Open calendar"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                <line x1="16" y1="2" x2="16" y2="6"/>
                                                <line x1="8" y1="2" x2="8" y2="6"/>
                                                <line x1="3" y1="10" x2="21" y2="10"/>
                                            </svg>
                                        </button>
                                    </div>
                                    {errors.dob && <div className="error-message admission_error">{errors.dob}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">YOUR AGE <span>*</span></label>
                                    <input
                                        type="text"
                                        name="age"
                                        value={formData.age}
                                        onChange={handleNumberInput}
                                        onKeyPress={handleKeyPress}
                                        className={`form-input admission_input ${errors.age ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">RESIDENTIAL ADDRESS <span>*</span></label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.address ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">CITY <span>*</span></label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.city ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">COUNTRY <span>*</span></label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.country ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">NATIONALITY <span>*</span></label>
                                    <input
                                        type="text"
                                        name="nationality"
                                        value={formData.nationality}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.nationality ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">CURRENT OCCUPATION <span>*</span></label>
                                    <input
                                        type="text"
                                        name="occupation"
                                        value={formData.occupation}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.occupation ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">EDUCATIONAL QUALIFICATION <span>*</span></label>
                                    <input
                                        type="text"
                                        name="education"
                                        value={formData.education}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.education ? 'error' : ''}`}

                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">COURSES YOU WANT TO ENROLL <span>*</span></label>

                                    {/* Course Selection Section */}
                                    <div className="form-input course-selection-section">
                                        <div className="course-select-row">
                                            <div className="course-select-item">
                                                <label className="form-label">Select Course</label>
                                                <select
                                                    value={currentCourse}
                                                    onChange={handleCourseChange}
                                                    className="form-input admission_input"
                                                >
                                                    <option value="">Choose a course</option>
                                                    <option value="opera">Opera</option>
                                                    <option value="western-vocals">Western Contemporary Vocals</option>
                                                    <option value="hindustani-vocals">Hindustani Classical Vocals</option>
                                                    <option value="bollywood">Bollywood Film Singing</option>
                                                    <option value="guitar">Guitar (Acoustic/Electric)</option>
                                                    <option value="violin">Violin</option>
                                                    <option value="piano">Piano/Keyboard</option>
                                                    <option value="music-production">Music Production</option>
                                                    <option value="dual">Dual Program</option>
                                                    <option value="all-in-one">All-In One Program</option>
                                                </select>
                                            </div>

                                            <div className="course-select-item">
                                                <label className="form-label">Course Type</label>
                                                <select
                                                    value={currentCourseType}
                                                    onChange={handleCourseTypeChange}
                                                    className="form-input admission_input"
                                                    disabled={!currentCourse}
                                                >
                                                    <option value="">Select type</option>
                                                    {getAvailableCourseTypes().map(courseType => (
                                                        <option key={courseType.value} value={courseType.value}>
                                                            {courseType.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="course-select-item">
                                                <button
                                                    type="button"
                                                    onClick={addCourse}
                                                    className="form-input add-course-btn"
                                                    disabled={!currentCourse || !currentCourseType}
                                                >
                                                    Add Course
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Selected Courses Display */}
                                    {formData.selectedCourses.length > 0 && (
                                        <div className="form-input selected-courses">
                                            <h4>Selected Courses:</h4>
                                            <div className="selected-courses-list">
                                                {formData.selectedCourses.map((courseItem, index) => (
                                                    <div key={index} className="selected-course-item">
                                                        <div className="course-info">
                                                            <span className="course-name">{getCourseLabel(courseItem.course)}</span>
                                                            <span className="course-type">({getCourseTypeLabel(courseItem.type)})</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCourse(index)}
                                                            className="remove-course-btn"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {errors.selectedCourses && <div className="error-message admission_error">{errors.selectedCourses}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">HAVE TAKEN ANY PROFESSIONAL TRAINING? IF YES, PLEASE MENTION WHICH COURSE, TUTOR/INSTITUTION NAME, TIME DURATION, SUBJECT AND OTHER NECESSARY DETAILS.</label>
                                    <textarea
                                        name="training"
                                        value={formData.training}
                                        onChange={handleInputChange}
                                        className="form-input admission_input"
                                        rows={4}

                                    ></textarea>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">WHAT IS YOUR GOAL FOR LEARNING MUSIC?</label>
                                    <textarea
                                        name="goal"
                                        value={formData.goal}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input`}
                                        rows={4}
                                    ></textarea>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">HOW DID YOU GET TO KNOW ABOUT US? <span>*</span></label>
                                    <textarea
                                        name="howKnow"
                                        value={formData.howKnow}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.howKnow ? 'error' : ''}`}
                                        rows={4}

                                    ></textarea>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">KINDLY ADD THE NATIONALITY ID</label>
                                    <input
                                        type="file"
                                        name="nationalityId"
                                        ref={nationalityIdRef}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.nationalityId ? 'error' : ''}`}
                                        accept="image/png,image/jpg,image/jpeg"

                                    />
                                    {formData.nationalityId && (
                                        <small>Selected: {formData.nationalityId.name}</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">PAYMENT METHOD <span>*</span></label>
                                    <div className="radio-group">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="cash"
                                                checked={formData.paymentMethod === 'cash'}
                                                onChange={handleInputChange}
                                                className="admission_input"
                                            />
                                            <span className="radio-text">Cash</span>
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="online"
                                                checked={formData.paymentMethod === 'online'}
                                                onChange={handleInputChange}
                                                className="admission_input"
                                            />
                                            <span className="radio-text">Online</span>
                                        </label>
                                    </div>
                                    {errors.paymentMethod && <div className="error-message admission_error">{errors.paymentMethod}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">KINDLY ADD PAYMENT SCREENSHOT OF TUITION FEE (THE TUITION FEE IS MENTIONED IN THE COURSE PAGE, OR GET IN TOUCH WITH YOUR COUNSELLOR FOR MORE CLARIFICATION ON FEE STRUCTURE) <span>*</span></label>
                                    <input
                                        type="file"
                                        name="paymentScreenshot"
                                        ref={paymentScreenshotRef}
                                        onChange={handleInputChange}
                                        className={`form-input admission_input ${errors.paymentScreenshot ? 'error' : ''}`}
                                        accept="image/png,image/jpg,image/jpeg"

                                    />
                                    {formData.paymentScreenshot && (
                                        <small>Selected: {formData.paymentScreenshot.name}</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <div className="checkbox-group">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="terms"
                                                checked={formData.terms}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="checkbox-text">I have read and agreed to all the terms and conditions <span>*</span></span>
                                        </label>
                                        {errors.terms && <div className="error-message admission_error">{errors.terms}</div>}

                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="privacy"
                                                checked={formData.privacy}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="checkbox-text">I have read and agreed to all the privacy policy <span>*</span></span>
                                        </label>
                                        {errors.privacy && <div className="error-message admission_error">{errors.privacy}</div>}

                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="notRobot"
                                                checked={formData.notRobot}
                                                onChange={handleInputChange}
                                                className="admission_input"

                                            />
                                            <span className="checkbox-text">I'm not a robot <span>*</span></span>
                                        </label>
                                        {errors.notRobot && <div className="error-message admission_error">{errors.notRobot}</div>}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <button
                                        type="submit"
                                        className={`submit-btn admission_submit ${isSubmitting ? 'submit-btn-loading' : ''}`}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="loading-spinner"></span>
                                                SUBMITTING...
                                            </>
                                        ) : (
                                            'SUBMIT'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
            
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Thank You!"
                message="We'll get back to you as soon as possible."
            />
        </>
    );
} 